import { AsyncState, Fun, Updater, unloadedAsyncState } from 'widgets-for-react'
import { List } from 'immutable'
import { getSponsorData, loadSharepointPageData } from './sharepointPageApi'
import { SponsorData } from '../sponsor/sponsorState'
import { ImageData } from '../image/imageState'
import { loadSponsorData } from '../sponsor/sponsorApi'
import { Block, BlocksState, initialBlocksState } from './blocks/blocksState'
import { initialComponentsBlockState } from './blocks/block/componentsBlock/componentsBlockState'

export type SharepointPageState = {
  sharepointPageDataState: AsyncState<SharepointPageData>
  sponsorDataState: AsyncState<SponsorData>
  blocksState: BlocksState
}

export type SharepointPageData = {
  ContentType: string
  CanvasContent1: string
  HeaderDescription: string
  HeaderImage: ImageData
  HeaderTitle: string
  Id: number
  PublicationDateBegin: string
  Public: boolean
  SubscriptionTypesId: number[]
  BrandId: number
}

export const initialSharepointPageState = (
  slug: string
): SharepointPageState => ({
  sharepointPageDataState: loadSharepointPageData(slug),
  sponsorDataState: unloadedAsyncState(),
  blocksState: initialBlocksState,
})

export const sharepointPageStateUpdaters = {
  sharepointPageDataStateUpdater:
    (updater: Updater<AsyncState<SharepointPageData>>) =>
    (state: SharepointPageState): SharepointPageState => {
      const newSharepointPageDataState = updater(state.sharepointPageDataState)
      return newSharepointPageDataState.kind === 'loaded' &&
        state.sharepointPageDataState.kind !== 'loaded'
        ? {
            ...state,
            sharepointPageDataState: newSharepointPageDataState,
            sponsorDataState: newSharepointPageDataState.value.BrandId
              ? loadSponsorData(() =>
                  getSponsorData(newSharepointPageDataState.value.BrandId)
                )
              : state.sponsorDataState,
            blocksState: {
              blocks: parseSharepointWebparts(
                newSharepointPageDataState.value.CanvasContent1
              ),
            },
          }
        : {
            ...state,
            sharepointPageDataState: newSharepointPageDataState,
          }
    },
  updateSponsorDataState:
    (updater: Updater<AsyncState<SponsorData>>) =>
    (state: SharepointPageState): SharepointPageState => ({
      ...state,
      sponsorDataState: updater(state.sponsorDataState),
    }),
  updateBlocksState:
    (
      updater: Fun<BlocksState, BlocksState & { reloadCollections?: boolean }>
    ) =>
    (
      state: SharepointPageState
    ): SharepointPageState & { reloadCollections?: boolean } => {
      const newBlocksState = updater(state.blocksState)
      return {
        ...state,
        blocksState: newBlocksState,
        reloadCollections: newBlocksState.reloadCollections,
      }
    },
}

export const parseSharepointWebparts = (canvasContent: string): List<any> => {
  try {
    const xmlParser = new DOMParser()
    const webpartsData = xmlParser.parseFromString(canvasContent, 'text/html')
    const webparts: any[] = []
    webpartsData.querySelectorAll('[data-sp-webpartdata]').forEach((e) => {
      const webpart = e.getAttribute('data-sp-webpartdata')
      if (webpart !== null) webparts.push(JSON.parse(webpart))
    })

    const blocks = webparts.map((data) => {
      const webpart = { ...data.properties }
      const searchablePlainTexts =
        data.serverProcessedContent?.searchablePlainTexts
      const searchableHtmlStrings = data.serverProcessedContent?.htmlStrings
      if (searchablePlainTexts) {
        for (const [key, value] of Object.entries(searchablePlainTexts)) {
          // Decode the html content for rich text field
          var decodedElement = document.createElement('p')
          decodedElement.innerHTML = value as string
          const decodedText = decodedElement.innerText
          webpart[key] = decodedText
        }
      }
      if (searchableHtmlStrings) {
        for (const [key, value] of Object.entries(searchableHtmlStrings)) {
          webpart[key] = value
        }
      }

      switch (webpart.kind) {
        case 'component-grid':
          return {
            ...webpart,
            componentsBlocksState: initialComponentsBlockState(
              webpart.components.map(
                (component: { id: number }) => component.id
              )
            ),
          }
        case 'media':
          return {
            ...webpart,
            items: webpart.items.map((item: any) => ({
              ...item,
              image: item.image
                ? { ...item.image, url: ensureHTTPS(item.image.url) }
                : undefined,
              tabletImage: item.tabletImage
                ? {
                    ...item.tabletImage,
                    url: ensureHTTPS(item.tabletImage?.url),
                  }
                : undefined,
              mobileImage: item.mobileImage
                ? {
                    ...item.mobileImage,
                    url: ensureHTTPS(item.mobileImage?.url),
                  }
                : undefined,
              videoUrl: item.videoUrl ? ensureHTTPS(item.videoUrl) : undefined,
            })),
          }
        case 'text_with_image':
          return {
            ...webpart,
            image: {
              ...webpart.image,
              url: ensureHTTPS(webpart.image.url),
            },
          }
        default:
          return webpart as Block
      }
    })
    return List(blocks)
  } catch (e) {
    console.error(e)
    return List<any>([])
  }
}

const ensureHTTPS = (url: string) => url.replace('http://', 'https://')
